import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";

type Product = {
  id: number;
  sku: string;
  name: string;
  unit: string | null;
  category: string | null;
  min_qty: string | null;
  cost_price: string | null;
  industry_id: number | null;
  note: string | null;
  active: boolean;
  pos_variation_id: string | null;
  pos_product_id: string | null;
  variation_detail: string | null;
  barcode: string | null;
  weight_g: number | null;
};

type Industry = { id: number; code: string; name: string };

const emptyForm = (): Partial<Product> => ({
  sku: "",
  name: "",
  unit: "cái",
  category: "",
  min_qty: null,
  industry_id: null,
  note: "",
  active: true,
});

export function ProductsPage() {
  const { user } = useAuth();
  const canWrite =
    user?.role === "admin" || user?.role === "ketoan" || user?.role === "kho";
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<Product> | null>(null);

  const listQ = useQuery({
    queryKey: ["products", search],
    queryFn: async () =>
      (await api.get<Product[]>("/products", { params: { q: search || undefined } })).data,
  });
  const industriesQ = useQuery({
    queryKey: ["industries"],
    queryFn: async () => (await api.get<Industry[]>("/industries")).data,
  });

  const save = useMutation({
    mutationFn: async (p: Partial<Product>) => {
      // Chỉ gửi các field user-editable (không động vào pos_* fields)
      const payload: any = {
        sku: p.sku,
        name: p.name,
        unit: p.unit,
        category: p.category,
        min_qty: p.min_qty,
        cost_price: p.cost_price,
        industry_id: p.industry_id,
        note: p.note,
        active: p.active,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (p.id) {
        const { sku, ...rest } = payload; // SKU không được sửa khi update
        return (await api.patch<Product>(`/products/${p.id}`, rest)).data;
      }
      return (await api.post<Product>("/products", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      setEditing(null);
    },
  });

  const importMut = useMutation({
    mutationFn: async () =>
      (await api.post<{ scanned_variations: number; created: number; updated: number }>(
        "/products/import-from-pos"
      )).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Sản phẩm</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Danh mục SP + giá vốn để tính lãi lỗ.
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <button
              onClick={() => importMut.mutate()}
              disabled={importMut.isPending}
              className="btn-secondary"
              title="Quét pos_orders.items_json, tự tạo sản phẩm theo variation_id"
            >
              {importMut.isPending
                ? "↻ Đang import..."
                : "↻ Import từ POScake"}
            </button>
            <button
              onClick={() => setEditing(emptyForm())}
              className="btn-primary"
            >
              + Thêm SP
            </button>
          </div>
        )}
      </div>

      {importMut.data && (
        <div className="mb-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 text-sm">
          ✓ Quét {importMut.data.scanned_variations} variation →{" "}
          <b>{importMut.data.created}</b> mới,{" "}
          <b>{importMut.data.updated}</b> update.
        </div>
      )}
      {importMut.error && (
        <div className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
          {(importMut.error as any)?.response?.data?.detail ??
            String(importMut.error)}
        </div>
      )}

      <div className="card p-3 mb-4">
        <input
          placeholder="Tìm theo SKU hoặc tên..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-md"
        />
      </div>

      <div className="card overflow-x-auto">
      <table className="table-modern">
        <thead>
          <tr>
            <th className="text-left px-3 py-2">SKU</th>
            <th className="text-left px-3 py-2">Tên</th>
            <th className="text-left px-3 py-2">Biến thể</th>
            <th className="text-left px-3 py-2">Barcode</th>
            <th className="text-left px-3 py-2">ĐVT</th>
            <th className="text-left px-3 py-2">Nhóm</th>
            <th className="text-right px-3 py-2">Giá vốn</th>
            <th className="text-left px-3 py-2">Ngành nghề</th>
            <th className="text-center px-3 py-2">Nguồn</th>
            <th className="text-center px-3 py-2">Active</th>
          </tr>
        </thead>
        <tbody>
          {listQ.isLoading && (
            <tr>
              <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                Đang tải...
              </td>
            </tr>
          )}
          {listQ.data?.length === 0 && (
            <tr>
              <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                Chưa có sản phẩm. Bấm "↻ Import từ đơn POScake" để tự tạo.
              </td>
            </tr>
          )}
          {listQ.data?.map((p) => (
            <tr
              key={p.id}
              className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
              onClick={() => canWrite && setEditing(p)}
            >
              <td className="px-3 py-2 font-mono text-xs">{p.sku}</td>
              <td className="px-3 py-2 font-medium">{p.name}</td>
              <td className="px-3 py-2 text-xs text-gray-600">{p.variation_detail ?? "—"}</td>
              <td className="px-3 py-2 font-mono text-xs">{p.barcode ?? "—"}</td>
              <td className="px-3 py-2">{p.unit ?? "—"}</td>
              <td className="px-3 py-2">{p.category ?? "—"}</td>
              <td
                className={`px-3 py-2 text-right font-mono text-xs ${
                  p.cost_price ? "text-gray-900" : "text-red-500"
                }`}
              >
                {p.cost_price
                  ? Number(p.cost_price).toLocaleString("vi-VN") + "đ"
                  : "⚠️ chưa có"}
              </td>
              <td className="px-3 py-2 text-xs text-gray-600">
                {industriesQ.data?.find((i) => i.id === p.industry_id)?.code ?? "—"}
              </td>
              <td className="px-3 py-2 text-center">
                {p.pos_variation_id ? (
                  <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded">
                    POScake
                  </span>
                ) : (
                  <span className="text-gray-400 text-xs">Manual</span>
                )}
              </td>
              <td className="px-3 py-2 text-center">{p.active ? "✓" : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {editing && (
        <EditDrawer
          form={editing}
          industries={industriesQ.data ?? []}
          onClose={() => setEditing(null)}
          onSave={(f) => save.mutate(f)}
          saving={save.isPending}
          error={save.error as any}
        />
      )}
    </div>
  );
}

function EditDrawer({
  form: initial,
  industries,
  onClose,
  onSave,
  saving,
  error,
}: {
  form: Partial<Product>;
  industries: Industry[];
  onClose: () => void;
  onSave: (f: Partial<Product>) => void;
  saving: boolean;
  error: any;
}) {
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="w-full max-w-lg bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {form.id ? "Sửa sản phẩm" : "Thêm sản phẩm"}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            ✕
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave(form);
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-2 gap-2">
            <Field
              label="SKU *"
              required
              value={form.sku ?? ""}
              onChange={(v) => setForm({ ...form, sku: v })}
            />
            <Field
              label="ĐVT"
              value={form.unit ?? ""}
              onChange={(v) => setForm({ ...form, unit: v })}
            />
          </div>
          <Field
            label="Tên *"
            required
            value={form.name ?? ""}
            onChange={(v) => setForm({ ...form, name: v })}
          />
          <div className="grid grid-cols-2 gap-2">
            <Field
              label="Nhóm"
              value={form.category ?? ""}
              onChange={(v) => setForm({ ...form, category: v })}
            />
            <Field
              label="Tồn min"
              value={form.min_qty ?? ""}
              onChange={(v) => setForm({ ...form, min_qty: v })}
            />
            <Field
              label="Giá vốn (đ/SP)"
              value={form.cost_price ?? ""}
              onChange={(v) => setForm({ ...form, cost_price: v })}
            />
          </div>
          <div className="text-[10px] text-slate-500 -mt-2">
            💡 <b>Mã hàng</b> được gán ở trang riêng: menu 🔖 Mã hàng
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Ngành nghề</label>
            <select
              value={form.industry_id ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  industry_id: e.target.value ? Number(e.target.value) : null,
                })
              }
              className="w-full border rounded px-2 py-1.5 text-sm"
            >
              <option value="">— Không —</option>
              {industries.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.code} — {i.name}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Ghi chú"
            textarea
            value={form.note ?? ""}
            onChange={(v) => setForm({ ...form, note: v })}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active ?? true}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Đang hoạt động
          </label>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
              {error?.response?.data?.detail ?? String(error)}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            >
              Huỷ
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  textarea?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-0.5">{label}</label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="w-full border rounded px-2 py-1.5 text-sm"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="w-full border rounded px-2 py-1.5 text-sm"
        />
      )}
    </div>
  );
}
